import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import HedgeCalculator from './HedgeCalculator';

export default function Hero() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="forside" className="relative min-h-[60vh] lg:min-h-[70vh] xl:min-h-[75vh] flex items-center justify-center pt-28 sm:pt-32 md:pt-36 lg:pt-40 pb-12 sm:pb-16 overflow-hidden bg-stone-950">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/4817d1f4-a80f-4dbe-aca5-664c58aee383.jpg"
          alt="Smuk nyanlagt have i Midt- og Vestjylland"
          className="w-full h-full object-cover object-center scale-125 animate-subtle-zoom"
        />
        <div 
          className="absolute inset-0 bg-stone-950/60" 
        />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 sm:pt-4 pb-2 flex flex-col justify-center h-full w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left column: Text & Info */}
          <div className="lg:col-span-7 text-center lg:text-left flex flex-col items-center lg:items-start">
            {/* Logo/Badge in Hero */}
            <img
              src="https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/40ef4765-67bb-4357-9f64-8a2aafc0bd52.png"
              alt="BP Anlægsgartner Logo"
              className="absolute top-1/2 left-1/2 lg:left-1/3 -translate-x-1/2 -translate-y-1/2 h-36 sm:h-[200px] md:h-[260px] lg:h-[300px] xl:h-[340px] 2xl:h-[380px] w-auto object-contain brightness-0 invert opacity-[0.10] pointer-events-none -z-10"
            />

            {/* Headline */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl font-extrabold text-white tracking-wider leading-tight mb-4 sm:mb-5 xl:mb-6 drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)] uppercase">
              Professionelt havearbejde med vestjysk stolthed
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-base md:text-lg lg:text-lg xl:text-xl text-stone-200 mb-6 sm:mb-8 xl:mb-10 leading-relaxed max-w-2xl lg:max-w-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
              Søger du en pålidelig gartner? Jeg hjælper husejere og sommerhusejere i hele Midt- og Vestjylland med alt fra smukke fliseterrasser og nyanlæg til løbende havepleje og vedligeholdelse.
            </p>

            {/* Bullet points / Trust badges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 w-full max-w-md lg:max-w-none text-left">
              <div className="flex items-center gap-2.5 text-stone-200">
                <div className="flex-shrink-0 p-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">Fast lav meterpris på hækklipning</span>
              </div>
              <div className="flex items-center gap-2.5 text-stone-200">
                <div className="flex-shrink-0 p-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">Professionelt udført arbejde fra start til slut</span>
              </div>
              <div className="flex items-center gap-2.5 text-stone-200">
                <div className="flex-shrink-0 p-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">Erfaren og pålidelig lokal gartner</span>
              </div>
              <div className="flex items-center gap-2.5 text-stone-200">
                <div className="flex-shrink-0 p-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">Uforpligtende tilbud inden for 24t</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start w-full sm:w-auto">
              <button
                onClick={() => scrollToSection('kontakt')}
                className="group flex items-center justify-center gap-2 text-xs sm:text-base font-semibold text-white bg-emerald-600 hover:bg-emerald-500 px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-full transition-all duration-300 shadow-lg shadow-emerald-900/40 hover:shadow-emerald-900/60 hover:-translate-y-1 cursor-pointer"
              >
                Få et gratis tilbud
                <ArrowRight className="h-3.5 w-3.5 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right column: Interactive Calculator */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end w-full">
            <HedgeCalculator />
          </div>

        </div>
      </div>
    </section>
  );
}
